import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Clock, AlertTriangle, XCircle, CheckCircle, RefreshCw, Calculator } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending: { label: 'Offen', icon: Clock, cls: 'bg-blue-100 text-blue-700' },
  needs_review: { label: 'Prüffall', icon: AlertTriangle, cls: 'bg-amber-100 text-amber-700' },
  not_eligible_before_referral: { label: 'Vor Berechtigung', icon: XCircle, cls: 'bg-gray-100 text-gray-600' },
  failed: { label: 'Fehler', icon: XCircle, cls: 'bg-red-100 text-red-700' },
  resolved: { label: 'Erledigt', icon: CheckCircle, cls: 'bg-green-100 text-green-700' },
};

const eur = (v) => `${Number(v || 0).toFixed(2)} €`;

const AffiliatePendingPayments = () => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  // Backpay tool
  const [bpAffiliate, setBpAffiliate] = useState('');
  const [bpPeriod, setBpPeriod] = useState('');
  const [bpPreview, setBpPreview] = useState(null);
  const [bpMsg, setBpMsg] = useState('');
  const [bpBusy, setBpBusy] = useState(false);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter ? `?status=${filter}` : '';
      const res = await axios.get(`${API_URL}/api/superadmin/affiliates/pending-payments${q}`, authHeaders());
      setItems(res.data.items || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const retry = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API_URL}/api/superadmin/affiliates/pending-payments/${id}/retry`, {}, authHeaders());
      await load();
    } catch (e) {
      alert('Neu-Verarbeitung fehlgeschlagen');
    } finally {
      setBusyId(null);
    }
  };

  const preview = async () => {
    setBpMsg(''); setBpPreview(null);
    if (!bpAffiliate || !bpPeriod) { setBpMsg('Affiliate-ID und Periode (YYYY-MM) erforderlich'); return; }
    setBpBusy(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/superadmin/affiliates/${bpAffiliate}/backpay-preview?period=${bpPeriod}`, authHeaders());
      setBpPreview(res.data);
    } catch (e) {
      setBpMsg(e.response?.data?.detail || 'Vorschau fehlgeschlagen');
    } finally {
      setBpBusy(false);
    }
  };

  const applyBackpay = async () => {
    if (!bpPreview) return;
    if (!window.confirm(`Nachzahlung ${eur(bpPreview.difference)} für ${bpPeriod} buchen?`)) return;
    setBpBusy(true); setBpMsg('');
    try {
      const res = await axios.post(
        `${API_URL}/api/superadmin/affiliates/${bpAffiliate}/backpay/apply`,
        { period: bpPeriod, expected_difference: bpPreview.difference }, authHeaders());
      setBpMsg(`Ergebnis: ${res.data.status} (${eur(res.data.booked_amount || 0)})`);
      await preview();
    } catch (e) {
      setBpMsg(e.response?.data?.detail?.message || e.response?.data?.detail || 'Buchung fehlgeschlagen');
    } finally {
      setBpBusy(false);
    }
  };

  return (
    <div className="mb-8 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden" data-testid="affiliate-pending-panel">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-blue-600" />
          <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">Offene Zahlungen & Prüffälle</h2>
        </div>
        <button onClick={load} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" title="Aktualisieren" data-testid="pending-refresh-btn">
          <RefreshCw className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-2 p-4 border-b border-gray-100 dark:border-gray-700">
        <button onClick={() => setFilter('')} className={`px-3 py-1 rounded-full text-xs font-medium ${filter === '' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`} data-testid="pending-filter-all">
          Alle ({Object.values(counts).reduce((a, b) => a + b, 0)})
        </button>
        {Object.keys(STATUS_META).map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`pending-filter-${s}`}
            className={`px-3 py-1 rounded-full text-xs font-medium ${filter === s ? 'bg-gray-900 text-white' : STATUS_META[s].cls}`}>
            {STATUS_META[s].label} ({counts[s] || 0})
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-6 text-center text-gray-500 text-sm">Laden…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center text-gray-500 text-sm" data-testid="pending-empty">Keine Einträge</div>
        ) : (
          <table className="w-full text-sm" data-testid="pending-table">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400">
                <th className="px-4 py-2 text-left font-semibold">Firma / Kunde</th>
                <th className="px-4 py-2 text-left font-semibold">Rechnung</th>
                <th className="px-4 py-2 text-right font-semibold">Netto</th>
                <th className="px-4 py-2 text-left font-semibold">Status</th>
                <th className="px-4 py-2 text-left font-semibold">Grund / Fehler</th>
                <th className="px-4 py-2 text-right font-semibold">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const meta = STATUS_META[it.status] || { label: it.status, cls: 'bg-gray-100 text-gray-600' };
                const canRetry = ['failed', 'needs_review', 'pending'].includes(it.status);
                return (
                  <tr key={it.id} className="border-t border-gray-100 dark:border-gray-700" data-testid={`pending-row-${it.stripe_invoice_id}`}>
                    <td className="px-4 py-2">
                      <div className="text-gray-900 dark:text-gray-100">{it.company_name || '—'}</div>
                      <div className="text-xs text-gray-400">{it.contact_email || it.customer_id}</div>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-600 dark:text-gray-400">{it.stripe_invoice_id}</td>
                    <td className="px-4 py-2 text-right">{eur((it.amount_net_cents || 0) / 100)}</td>
                    <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${meta.cls}`}>{meta.label}</span></td>
                    <td className="px-4 py-2 text-xs text-gray-500 max-w-xs truncate" title={it.last_error || it.reviewed_reason || ''}>
                      {it.last_error || it.reviewed_reason || (it.attempts ? `Versuche: ${it.attempts}` : '')}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {canRetry && (
                        <button onClick={() => retry(it.id)} disabled={busyId === it.id}
                          data-testid={`pending-retry-${it.stripe_invoice_id}`}
                          className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                          {busyId === it.id ? '…' : 'Neu verarbeiten'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Backpay tool for closed periods */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <div className="flex items-center gap-2 mb-3">
          <Calculator className="w-4 h-4 text-purple-600" />
          <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">Nachzahlung abgeschlossener Perioden (Staffel-Differenz)</h3>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Affiliate-ID</label>
            <input value={bpAffiliate} onChange={(e) => setBpAffiliate(e.target.value.trim())} data-testid="backpay-affiliate-input"
              className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm w-64" placeholder="affiliate id" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Periode</label>
            <input value={bpPeriod} onChange={(e) => setBpPeriod(e.target.value.trim())} data-testid="backpay-period-input"
              className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm w-32" placeholder="YYYY-MM" />
          </div>
          <button onClick={preview} disabled={bpBusy} data-testid="backpay-preview-btn"
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm disabled:opacity-50">Berechnen</button>
        </div>

        {bpMsg && <div className="mt-3 text-sm text-gray-700 dark:text-gray-300" data-testid="backpay-msg">{bpMsg}</div>}

        {bpPreview && (
          <div className="mt-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg" data-testid="backpay-preview">
            <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
              <div>Netto-Umsatz {bpPeriod}: <b>{eur(bpPreview.revenue_total_net)}</b> → Staffel <b>{Math.round((bpPreview.tier_rate || 0) * 100)}%</b> ({bpPreview.tier_percent})</div>
              <div>Korrekte Gesamtprovision: <b>{eur(bpPreview.correct_total_commission)}</b></div>
              <div>Bereits ausgezahlt: {eur(bpPreview.already_paid)} · bereits nachgebucht: {eur(bpPreview.already_booked_backpay)}</div>
              <div className="text-base">Noch auszugleichende Differenz: <b className="text-purple-700">{eur(bpPreview.difference)}</b></div>
              {!bpPreview.period_closed && <div className="text-amber-600 text-xs">Periode ist nicht abgeschlossen – Buchung nicht möglich.</div>}
            </div>
            {bpPreview.period_closed && Number(bpPreview.difference) > 0 && (
              <button onClick={applyBackpay} disabled={bpBusy} data-testid="backpay-apply-btn"
                className="mt-3 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:opacity-50">
                Differenz {eur(bpPreview.difference)} buchen
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliatePendingPayments;
